import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import AddItemFormDialog from "../components/FormDialog/AddItemFormDialog";
import AddOrderFormDialog from "../components/FormDialog/AddOrderFormDialog";
import OrderItemFormDialog from "../components/FormDialog/OrderItemFormDialog";
import CollapseTable from "../components/Table/CollapseTable";
import api from "../components/api";
import moment from "moment";
import { Order, OrderOrderItem, User } from "../api";
import AddOrderOrderItemFormDialog from "../components/FormDialog/AddOrderOrderItemFormDialog";
import EditOrderFormDialog from "../components/FormDialog/EditOrderFormDialog";
import DeleteFormDialog from "../components/FormDialog/DeleteFormDialog";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import InfoIcon from "@mui/icons-material/Info";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ReorderIcon from "@mui/icons-material/Reorder";
import { config } from "../components/api";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import GenericTable from "../components/Table/Table";
import Calendar from "../components/Calendar/Calendar";
import dayjs, { Dayjs } from "dayjs";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CalendarList from "../components/Calendar/CalendarList";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

const OrderPage: React.FC = () => {
  const [refreshTrigger, setRefreshTrigger] = React.useState(false);

  const { user_id } = useParams<{ user_id: string }>();
  const location = useLocation();
  const isAllOrdersPage = location.pathname === "/order";
  const clientNameFromState = location.state?.client_name;
  const [clientName, setClientName] = React.useState<string>(
    clientNameFromState || "",
  );

  const [openCreateDialog, setOpenCreateDialog] = React.useState(false);
  const [openCreateOrderDialog, setOpenCreateOrderDialog] =
    React.useState(false);
  const [openOrderItemDialog, setOpenOrderItemDialog] = React.useState(false);
  const [orders, setOrders] = React.useState<any[]>([]);
  const [openEditOrderDialog, setOpenEditOrderDialog] = React.useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
  const [openDeleteOrderOrderItemDialog, setOpenDeleteOrderOrderItemDialog] =
    React.useState(false);
  const [openAddOrderOrderItemDialog, setOpenAddOrderOrderItemDialog] =
    React.useState(false);
  const [orderToEdit, setOrderToEdit] = React.useState<Order | null>(null);
  const [orderToDelete, setOrderToDelete] = React.useState<Order | null>(null);
  const [orderOrderItemToDelete, setOrderOrderItemToDelete] =
    React.useState<OrderOrderItem | null>(null);
  const [orderOrderItemToAdd, setOrderOrderItemToAdd] =
    React.useState<any>(null);
  const [selectedUser, setSelectedUser] = React.useState<any>(null);
  const [selectedDate, setSelectedDate] = React.useState<Dayjs | null>(null);
  const [highlightedDays, setHighlightedDays] = React.useState<Dayjs[]>([]);
  const [isCalendarMode, setIsCalendarMode] = React.useState(true);
  const [isCalendarVisible, setIsCalendarVisible] = React.useState(true);

  const [clientUsers, setClientUsers] = React.useState<User[]>([]);
  const [selectedClientName, setSelectedClientName] =
    React.useState<string>("");

  const [itemsUpdated, setItemsUpdated] = React.useState(false);

  const fetchOrdersByDate = async (date: Dayjs) => {
    try {
      const order_date = date.format("YYYY-MM-DD");
      let ordersData = [];

      if (config.role === "staff") {
        const [orderResponse, orderStaffResponse] = await Promise.all([
          api.orders.getOrdersOrdersGet(order_date),
          api.orderStaffs.getUsersOrderStaffsUsersOrderStaffsGet(),
        ]);

        const ordersContents = orderResponse.data.contents;
        const orderStaffContents = orderStaffResponse.data.contents;

        const commonOrders = ordersContents.filter((order) =>
          orderStaffContents.some(
            (orderStaff) => order.order_id === orderStaff.order_id,
          ),
        );
        if (commonOrders.length === 0) {
          console.warn(
            "No common orders found between ordersContents and orderStaffContents",
          );
        }

        ordersData = await Promise.all(
          commonOrders.map(async (order: any) => {
            const orderItemsResponse =
              await api.orderOrderItems.getOrdersOrderIdOrderOrderItemsOrdersOrderIdOrderOrderItemsGet(
                order.order_id,
              );
            const orderItems = orderItemsResponse.data.contents.map(
              (item: any) => ({
                ...item,
                description: item.order_item.description || "No description",
                price: item.order_item.price || 0,
                item_status: item.item_status || "Unknown",
                approval_date: item.approval_date
                  ? moment(item.approval_date).format("YYYY-MM-DD")
                  : "Pending Approval",
              }),
            );

            return {
              ...order,
              date: moment(order.date).format("YYYY/MM/DD"),
              name: order.users
                ? order.users.length > 1
                  ? order.users.map((user: any) => user.first_name).join(" & ")
                  : `${order.users[0].first_name} ${order.users[0].last_name}`
                : "Unknown User",
              status: order.order_status?.status || "Unknown",
              order_order_items: orderItems,
              public_note: order.public_note || "",
              private_note: order.private_note || "",
            };
          }),
        );
      } else {
        const response = await api.orders.getOrdersOrdersGet(order_date);
        const ordersContents = response.data.contents;

        ordersData = await Promise.all(
          ordersContents.map(async (order: any) => {
            const orderItemsResponse =
              await api.orderOrderItems.getOrdersOrderIdOrderOrderItemsOrdersOrderIdOrderOrderItemsGet(
                order.order_id,
              );
            const orderItems = orderItemsResponse.data.contents.map(
              (item: any) => ({
                ...item,
                description: item.order_item.description || "No description",
                price: item.order_item.price || 0,
                item_status: item.item_status || "Unknown",
                approval_date: item.approval_date
                  ? moment(item.approval_date).format("YYYY-MM-DD")
                  : "Pending Approval",
              }),
            );

            return {
              ...order,
              date: moment(order.date).format("YYYY/MM/DD"),
              name: order.users
                ? order.users.length > 1
                  ? order.users.map((user: any) => user.first_name).join(" & ")
                  : `${order.users[0].first_name} ${order.users[0].last_name}`
                : "Unknown User",
              status: order.order_status?.status || "Unknown",
              order_order_items: orderItems,
              public_note: order.public_note || "",
              private_note: order.private_note || "",
            };
          }),
        );
      }
      setOrders(ordersData);
    } catch (error) {
      console.error("Error fetching orders by date:", error);
    }
  };

  const hasDateParam = new URLSearchParams(location.search).has("date");

  const fetchOrders = async () => {
    try {
      let response;
      let orders: any[] = [];
      let orderStaff: any[] = [];

      if (config.role === "staff") {
        const orderStaffResponse =
          await api.orderStaffs.getUsersOrderStaffsUsersOrderStaffsGet();
        const orderStaffContents = orderStaffResponse.data.contents;

        orderStaff = orderStaffContents
          .filter((orderStaff) => Object.keys(orderStaff.order).length > 0)
          .map((orderStaff: any) => ({
            status: orderStaff.order.status,
            date: orderStaff.order.date,
            type: orderStaff.order.type,
            user_id: orderStaff.user_id,
            order_id: orderStaff.order_id,
            name:
              orderStaff.order.users?.length > 1
                ? orderStaff.order.users
                    .map((user: any) => user.first_name)
                    .join(" & ")
                : orderStaff.order.users?.length === 1
                  ? `${orderStaff.order.users[0].first_name} ${orderStaff.order.users[0].last_name}`
                  : "Unknown User",
          }));

        interface OrderStatus {
          complete_date?: string;
          description?: string;
          status_rank?: number;
          status_category?: string;
          status?: string;
          deadline?: string | null;
          receiver?: any[];
        }

        interface OrderUser {
          first_name: string;
          last_name: string;
          role: string;
          user_id: string;
        }

        interface OrderData {
          date: string;
          type?: string;
          user_id: string;
          self_link: string;
          kind: string;
          order_id: string;
          users: User[];
          order_status?: OrderStatus;
          public_note?: string;
          private_note?: string;
        }

        for (const orderStaff of orderStaffContents) {
          if (Object.keys(orderStaff.order).length > 0 && orderStaff.order_id) {
            const orderResponse = await api.orders.getOrderOrdersOrderIdGet(
              orderStaff.order_id,
            );
            const orderData: OrderData = {
              ...orderResponse.data,
              users: orderResponse.data.users,
              user_id: orderResponse.data.users[0].user_id || "",
              date: orderResponse.data.date || "",
              type: orderResponse.data.type || "",
              order_status: orderResponse.data.order_status || {},
              public_note: orderResponse.data.public_note || "",
              private_note: orderResponse.data.private_note || "",
            };

            const orderItemsResponse =
              await api.orderOrderItems.getOrdersOrderIdOrderOrderItemsOrdersOrderIdOrderOrderItemsGet(
                orderStaff.order_id,
              );
            const orderItems = orderItemsResponse.data.contents.map(
              (item: any) => ({
                ...item,
                description: item.order_item.description || "No description",
                price: item.order_item.price || 0,
                item_status: item.item_status || "Unknown",
                approval_date: item.approval_date
                  ? moment(item.approval_date).format("YYYY-MM-DD")
                  : "Pending Approval",
              }),
            );

            const orderStatus = orderData.order_status || {};

            const usersList = orderData.users || [];
            const name =
              usersList.length > 1
                ? usersList.map((user: any) => user.first_name).join(" & ")
                : usersList.length === 1
                  ? `${usersList[0].first_name} ${usersList[0].last_name}`
                  : "Unknown User";

            orders.push({
              order_status: {
                complete_date: orderStatus.complete_date,
                description: orderStatus.description,
                status_rank: orderStatus.status_rank,
                status_category: orderStatus.status_category,
                status: orderStatus.status,
                deadline: orderStatus.deadline,
                receiver: orderStatus.receiver,
              },
              status: orderData.order_status?.status || "Unknown",
              date: orderData.date,
              type: orderData.type,
              user_id: orderData.user_id,
              order_id: orderData.order_id,
              name: name,
              order_order_items: orderItems,
              public_note: orderData.public_note || "",
              private_note: orderData.private_note || "",
            });
          }
        }
        setOrders(orders);
      } else {
        if (user_id) {
          response = user_id
            ? await api.orders.getUserUserIdOrdersUsersUserIdOrdersGet(user_id)
            : await api.orders.getOrdersOrdersGet();
        } else {
          response = await api.orders.getOrdersOrdersGet();
        }

        const ordersContents = response.data.contents;

        orders = await Promise.all(
          ordersContents.map(async (order: any) => {
            const orderItemsResponse =
              await api.orderOrderItems.getOrdersOrderIdOrderOrderItemsOrdersOrderIdOrderOrderItemsGet(
                order.order_id,
              );
            const orderItems = orderItemsResponse.data.contents.map(
              (item: any) => ({
                ...item,
                description: item.order_item.description || "No description",
                price: item.order_item.price || 0,
                item_status: item.item_status || "Unknown",
                approval_date: item.approval_date
                  ? moment(item.approval_date).format("YYYY-MM-DD")
                  : "Pending Approval",
              }),
            );

            const orderDate = moment(order.date).format("YYYY/MM/DD");
            const status = order.order_status?.status || "Unknown";

            const usersList = Array.isArray(order.users)
              ? order.users
              : [order.user];
            const name =
              usersList.length > 1
                ? usersList.map((user: any) => user.first_name).join(" & ")
                : usersList.length === 1
                  ? `${usersList[0].first_name} ${usersList[0].last_name}`
                  : "Unknown User";

            return {
              ...order,
              date: orderDate,
              name: name,
              status: status,
              order_order_items: orderItems,
              public_note: order.public_note || "",
              private_note: order.private_note || "",
            };
          }),
        );
        setOrders(orders);
        if (!clientName && orders.length > 0) {
          const userClient = orders[0].users.find(
            (user: any) => user.role === "client",
            (user: any) => user.user_id === user_id,
          );
          const clientName = userClient
            ? `${userClient.first_name} ${userClient.last_name}`
            : "Unknown User";
          setClientName(clientName);
        }
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.users.getUsersUsersGet();

      const clients = response.data.contents.filter(
        (user: User) => user.role === "client",
      );
      setClientUsers(clients);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  React.useEffect(() => {
    if (!hasDateParam) {
      fetchOrders();
    } else if (selectedDate) {
      fetchOrdersByDate(selectedDate);
    } else {
      fetchOrders();
    }

    if (config.role === "admin" || config.role === "manager") {
      fetchUsers();
    } else {
      if (selectedDate) {
        fetchOrdersByDate(selectedDate);
      } else {
        fetchOrders();
      }
    }
  }, [user_id, selectedDate, hasDateParam, refreshTrigger]);

  const fetchOrdersByName = async () => {
    try {
      const response = await api.orders.getOrdersOrdersGet();
      const ordersContents = response.data.contents;

      const formattedOrders = await Promise.all(
        ordersContents.map(async (order: any) => {
          const orderItemsResponse =
            await api.orderOrderItems.getOrdersOrderIdOrderOrderItemsOrdersOrderIdOrderOrderItemsGet(
              order.order_id,
            );
          const orderItems = orderItemsResponse.data.contents.map(
            (item: any) => ({
              ...item,
              description: item.order_item.description || "No description",
              price: item.order_item.price || 0,
              item_status: item.item_status || "Unknown",
              approval_date: item.approval_date
                ? moment(item.approval_date).format("YYYY-MM-DD")
                : "Pending Approval",
            }),
          );

          const usersList = order.users || [];
          const combinedNames = usersList
            .map((user: any) => `${user.first_name} ${user.last_name}`)
            .join(" & ");

          return {
            ...order,
            name: combinedNames,
            date: moment(order.date).format("YYYY/MM/DD"),
            dateForSearch: moment(order.date).format("YYYYMMDD"),
            status: order.order_status?.status || "Unknown",
            order_order_items: orderItems,
            public_note: order.public_note || "",
            private_note: order.private_note || "",
          };
        }),
      );

      setOrders(formattedOrders);
    } catch (error) {
      console.error("Error fetching orders:", error);
    }
  };

  const filterOrdersByDatePattern = (pattern: string, orders: any[]) => {
    return orders.map((order) => {
      const orderDate = moment(order.date).format("YYYYMMDD");
      const isMatch = (() => {
        switch (pattern.length) {
          case 1:
            return orderDate.startsWith(pattern);
          case 2:
            return orderDate.startsWith(pattern);
          case 3:
            return orderDate.startsWith(pattern);
          case 4:
            return orderDate.startsWith(pattern);
          case 5:
            return orderDate.startsWith(pattern);
          case 6:
            return orderDate.startsWith(pattern);
          case 7:
            return orderDate.startsWith(pattern);
          case 8:
            return orderDate === pattern;
          default:
            return orderDate.startsWith(pattern);
        }
      })();

      return {
        ...order,
        hidden: !isMatch,
      };
    });
  };

  const filterOrdersByClientName = async (searchText: string) => {
    try {
      await fetchOrdersByName();

      if (searchText) {
        const isNumeric = /^\d+$/.test(searchText);

        if (isNumeric) {
          setOrders((prevOrders) =>
            filterOrdersByDatePattern(searchText, prevOrders),
          );
        } else {
          setOrders((prevOrders) =>
            prevOrders.map((order) => ({
              ...order,
              hidden: !order.name
                .toLowerCase()
                .includes(searchText.toLowerCase()),
            })),
          );
        }
      } else {
        setOrders((prevOrders) =>
          prevOrders.map((order) => ({
            ...order,
            hidden: false,
          })),
        );
      }
    } catch (error) {
      console.error("Error filtering orders:", error);
    }
  };

  const handleRefresh = (newOrderDates?: dayjs.Dayjs[]) => {
    setRefreshTrigger((prev) => !prev);
    if (newOrderDates) {
      setHighlightedDays((prev) => [...prev, ...newOrderDates]);
    }
  };

  React.useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const userResponse = await api.users.getUserUserGet();
        setSelectedUser(userResponse.data);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération de l'utilisateur :",
          error,
        );
      }
    };

    fetchCurrentUser();
  }, []);

  const handleOpenEditOrderDialog = (row: any) => {
    setOrderToEdit(row);
    setOpenEditOrderDialog(true);
  };

  const handleCloseEditOrderDialog = () => {
    setOrderToEdit(null);
    setOpenEditOrderDialog(false);
  };

  const handleOpenDeleteDialog = (row: any) => {
    setOrderToDelete(row);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOrderToDelete(null);
    setOpenDeleteDialog(false);
  };

  const handleOpenDeleteOrderOrderItemDialog = (
    row: any,
    orderOrderItem: OrderOrderItem,
  ) => {
    setOrderToDelete(row);
    setOrderOrderItemToDelete(orderOrderItem);
    setOpenDeleteOrderOrderItemDialog(true);
  };

  const handleCloseDeleteOrderOrderItemDialog = () => {
    setOrderOrderItemToDelete(null);
    setOpenDeleteOrderOrderItemDialog(false);
  };

  const handleOpenAddOrderOrderItemDialog = (row: any) => {
    setOrderOrderItemToAdd(row);
    setOpenAddOrderOrderItemDialog(true);
  };

  const handleCloseAddOrderOrderItemDialog = () =>
    setOpenAddOrderOrderItemDialog(false);

  const handleSaveEditOrder = (updatedOrder: Order) => {
    setOrders((prevOrders) => {
      return prevOrders.map((order) =>
        order.order_id === updatedOrder.order_id ? updatedOrder : order,
      );
    });
    setOpenEditOrderDialog(false);
    handleRefresh();
  };

  const handleDeleteOrderClick = () => {
    if (orderToDelete && handleDeleteOrder) {
      handleDeleteOrder(orderToDelete.order_id);
      handleCloseDeleteDialog();
    }
  };

  const handleDeleteOrderOrderItemClick = () => {
    if (orderOrderItemToDelete) {
      handleCloseDeleteOrderOrderItemDialog();
    }
  };

  const formatOrderOrderItems = (orderOrderItems: any) => {
    return orderOrderItems.map((item: any) => ({
      ...item,
      description: item.order_item.description,
      price: item.order_item.price,
      approval_date: item.approval_date
        ? item.approval_date
        : "Pending Approval",
    }));
  };

  const formatOrder = (order: any) => {
    return {
      ...order,
      order_order_items: formatOrderOrderItems(order.order_order_items),
    };
  };

  const handleAddOrderOrderItem = (updatedOrder: Order) => {
    const formattedOrder = formatOrder(updatedOrder);
    setOrders((prevOrders) => {
      const newOrders = prevOrders.map((order) =>
        order.order_id === formattedOrder.order_id ? formattedOrder : order,
      );
      return newOrders;
    });
    handleCloseAddOrderOrderItemDialog();
  };

  const handleCreateItemClick = () => setOpenCreateDialog(true);
  const handleCreateOrderClick = () => setOpenCreateOrderDialog(true);
  const handleOrderItemClick = () => setOpenOrderItemDialog(true);
  const handleCloseCreateDialog = () => setOpenCreateDialog(false);
  const handleCloseCreateOrderDialog = () => {
    setOpenCreateOrderDialog(false);
    setItemsUpdated(false);
  };
  const handleCloseOrderItemDialog = () => setOpenOrderItemDialog(false);

  const handleAddOrder = (newOrder: any) => setOrders([newOrder, ...orders]);

  const handleEditOrder = (updatedOrder: any) => {
    setOrders((prevOrders) => {
      const updatedOrders = prevOrders.map((order) =>
        order.order_id === updatedOrder.order_id ? updatedOrder : order,
      );
      updatedOrders.sort((a, b) =>
        a.user_id === updatedOrder.order_id
          ? -1
          : b.user_id === updatedOrder.order_id
            ? 1
            : 0,
      );
      return updatedOrders;
    });
  };

  const handleDeleteOrder = (deletedOrderId: string) => {
    setOrders((prevOrders) => {
      const updatedOrders = prevOrders.filter(
        (order) => order.order_id !== deletedOrderId,
      );
      return updatedOrders;
    });
  };

  const columnConfig = [
    { id: "expand", label: "", minWidth: 30 },
    { id: "name", label: "Name", minWidth: 100 },
    { id: "status", label: "Status", minWidth: 50 },
    { id: "date", label: "Date", minWidth: 50 },
    { id: "type", label: "Type", minWidth: 50 },
    { id: "public_note", label: "Note", minWidth: 100 },
    ...(config.role !== "client"
      ? [{ id: "private_note", label: "Private Note", minWidth: 100 }]
      : []),
    { id: "actions", label: "Actions", minWidth: 100, align: "center" },
  ];

  const detailColumnsAdmin = [
    { id: "description", label: "Description", minWidth: 100 },
    { id: "price", label: "Price", minWidth: 100 },
    { id: "item_status", label: "Item Status", minWidth: 100 },
    { id: "approval_date", label: "Approval Date", minWidth: 150 },
    { id: "actions", label: "Actions", minWidth: 100, align: "center" },
  ];

  const detailColumnsClient = [
    { id: "description", label: "Description", minWidth: 100 },
    { id: "price", label: "Price", minWidth: 100 },
    { id: "item_status", label: "Item Status", minWidth: 100 },
    { id: "approval_date", label: "Approval Date", minWidth: 150 },
  ];

  const getRows = async () => {
    let orders: any[] = [];
    let allOrderItems: any[] = [];
    let orderStaff: any[] = [];

    if (config.role === "staff") {
      const orderStaffResponse =
        await api.orderStaffs.getUsersOrderStaffsUsersOrderStaffsGet();
      const orderStaffContents = orderStaffResponse.data.contents;

      orderStaff = orderStaffContents
        .filter((orderStaff) => Object.keys(orderStaff.order).length > 0)
        .map((orderStaff: any) => ({
          status: orderStaff.order.status,
          date: orderStaff.order.date,
          type: orderStaff.order.type,
          user_id: orderStaff.user_id,
          order_id: orderStaff.order_id,
          user: {
            first_name: orderStaff.user.first_name,
            last_name: orderStaff.user.last_name,
          },
        }));

      interface OrderData {
        date: string;
        type?: string;
        user_id: string;
        self_link: string;
        kind: string;
        order_id: string;
        users?: OrderUser[];
        order_status?: OrderStatus;
      }

      interface OrderStatus {
        complete_date?: string;
        description?: string;
        status_rank?: number;
        status_category?: string;
        status?: string;
        deadline?: string | null;
        receiver?: any[];
      }

      interface OrderUser {
        first_name?: string;
        last_name?: string;
      }

      for (const orderStaff of orderStaffContents) {
        if (Object.keys(orderStaff.order).length > 0 && orderStaff.order_id) {
          const orderResponse = await api.orders.getOrderOrdersOrderIdGet(
            orderStaff.order_id,
          );
          const orderData: OrderData = {
            ...orderResponse.data,
            users: Array.isArray(orderResponse.data.users)
              ? orderResponse.data.users
              : [orderResponse.data.users],
            user_id: orderResponse.data.users[0]?.user_id || "",
            date: orderResponse.data.date || "",
            type: orderResponse.data.type || "",
            order_status: orderResponse.data.order_status || {},
          };

          const orderOrderItemResponse =
            await api.orderOrderItems.getOrdersOrderIdOrderOrderItemsOrdersOrderIdOrderOrderItemsGet(
              orderStaff.order_id,
            );
          allOrderItems = orderOrderItemResponse.data.contents;
          const orderItems = allOrderItems.filter(
            (item) => item.order_id === orderStaff.order_id,
          );

          const usersList = orderData.users || [];
          const name =
            usersList.length > 1
              ? usersList.map((user: any) => user.first_name).join(" & ")
              : `${usersList[0].first_name} ${usersList[0].last_name}`;

          orders.push({
            order_status: {
              complete_date: orderData.order_status?.complete_date,
              description: orderData.order_status?.description,
              status_rank: orderData.order_status?.status_rank,
              status_category: orderData.order_status?.status_category,
              status: orderData.order_status?.status,
              deadline: orderData.order_status?.deadline,
              receiver: orderData.order_status?.receiver,
            },
            status: orderData.order_status?.status || "Unknown",
            date: orderData.date,
            type: orderData.type,
            user_id: orderData.user_id,
            order_id: orderData.order_id,
            name: name,
            order_order_items: orderItems,
          });
        }
      }
    } else {
      if (user_id) {
        const response =
          await api.orders.getUserUserIdOrdersUsersUserIdOrdersGet(user_id);
        orders = response.data.contents;
      } else {
        const response = await api.orders.getOrdersOrdersGet();
        orders = response.data.contents;
      }

      const orderOrderItemResponse =
        await api.orderOrderItems.getOrderOrderItemsOrderOrderItemsGet();
      allOrderItems = orderOrderItemResponse.data.contents;
    }

    return orders.map((order: any) => {
      const orderDate = moment(order.date).format("YYYY/MM/DD");

      const orderItems =
        config.role !== "staff"
          ? allOrderItems.filter((item) => item.order_id === order.order_id)
          : [];

      const usersList = order.users || [];
      const name =
        usersList.length > 1
          ? usersList.map((user: any) => user.first_name).join(" & ")
          : `${usersList[0].first_name} ${usersList[0].last_name}`;

      const updatedOrderItems = orderItems.map((item: any) => ({
        ...item,
        order_item: {
          ...item.order_item,
          item_status: item.item_status || "Unknown",
          approval_date: item.approval_date
            ? moment(item.approval_date).format("YYYY-MM-DD")
            : "Pending Approval",
        },
      }));

      return {
        ...order,
        name: name,
        date: orderDate,
        order_order_items: updatedOrderItems,
        public_note: order.public_note || "",
        private_note: order.private_note || "",
      };
    });
  };

  const navigate = useNavigate();

  const handleOrderAnswerClick = (row: any) => {
    navigate(`/order_answer/${row.order_id}`, { state: { row } });
  };

  const handleOrderInfoClick = (row: any) => {
    navigate(`/order_info/${row.order_id}`, { state: { row } });
  };

  const getActionsButton = (row: any) => {
    return (
      <>
        <IconButton
          aria-label="edit"
          size="small"
          color="primary"
          onClick={() => {
            handleOpenEditOrderDialog(row);
          }}
        >
          <EditIcon />
        </IconButton>
        {(config.role === "admin" || config.role === "manager") && (
          <>
            <IconButton
              aria-label="delete"
              size="small"
              onClick={() => handleOpenDeleteDialog(row)}
            >
              <DeleteIcon />
            </IconButton>
            <IconButton
              aria-label="info"
              size="small"
              onClick={() => handleOrderInfoClick(row)}
            >
              <InfoIcon />
            </IconButton>
          </>
        )}
        <IconButton
          aria-label="info"
          size="small"
          onClick={() => handleOrderAnswerClick(row)}
        >
          <QuestionAnswerIcon />
        </IconButton>
        {row.type === "wedding" && (
          <IconButton
            aria-label="wedding-table"
            size="small"
            onClick={async () => {
              try {
                const weddingResponse =
                  await api.weddings.getWeddingByOrderIdWeddingTableOrderOrderIdGet(
                    row.order_id,
                  );
                const wedding = weddingResponse.data;

                navigate(`/wedding_table/${wedding.wedding_table_id}/table-list`);
              } catch (error) {
                console.error("Error fetching wedding:", error);
              }
            }}
          >
            <TableRestaurantIcon />
          </IconButton>
        )}
      </>
    );
  };

  const handleDateChange = (date: Dayjs | null) => {
    setSelectedDate(date);
  };

  const handleToggleView = () => {
    setIsCalendarMode((prev) => !prev);
  };

  const toggleCalendar = () => {
    setIsCalendarVisible((prev) => !prev);
  };

  const handleItemAdded = () => {
    setItemsUpdated(true);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ marginBottom: "1rem" }}
      >
        <Typography variant="h5">
          {user_id ? `${clientName}'s Orders` : "Order"}
        </Typography>
        {(config.role === "admin" || config.role === "manager") && (
          <Box sx={{ display: "flex", gap: "0.5rem" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOrderItemClick}
            >
              Order Items
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateItemClick}
            >
              Add Item
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateOrderClick}
            >
              Add Order
            </Button>
          </Box>
        )}
      </Box>

      {(config.role === "admin" || config.role === "manager") && (
        <>
          <Autocomplete
            options={clientUsers.map(
              (user) => `${user.first_name} ${user.last_name}`,
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search by Client Name or Date (YYYYMMDD)"
                onChange={(e) => {
                  const searchText = e.target.value;
                  setSelectedClientName(searchText);
                  filterOrdersByClientName(searchText);
                }}
              />
            )}
            value={selectedClientName}
            onChange={(event, newValue) => {
              setSelectedClientName(newValue || "");
              filterOrdersByClientName(newValue || "");
            }}
            freeSolo
            sx={{ marginBottom: "1rem" }}
          />
        </>
      )}

      <Grid container spacing={2}>
        <Grid item xs={user_id ? 12 : 8}>
          {config.role === "staff" ? (
            <GenericTable
              columnsConfig={columnConfig}
              tableType={"order"}
              rows={orders}
              getRows={getRows}
              setRows={setOrders}
              getActionsButton={getActionsButton}
            />
          ) : (
            <CollapseTable
              columnsConfig={columnConfig}
              detailColumns={
                config.role === "client"
                  ? detailColumnsClient
                  : detailColumnsAdmin
              }
              tableType="order"
              rows={orders.filter((order) => !order.hidden)}
              getRows={getRows}
              setRows={setOrders}
              handleOpenAddOrderOrderItemDialog={
                handleOpenAddOrderOrderItemDialog
              }
              handleOpenDeleteOrderOrderItemDialog={
                handleOpenDeleteOrderOrderItemDialog
              }
              getActionsButton={getActionsButton}
            />
          )}
        </Grid>

        {!user_id && isCalendarVisible && (
          <Grid item xs={4}>
            <Card>
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="h6">Calendar View</Typography>
                  <IconButton onClick={handleToggleView}>
                    {isCalendarMode ? <ReorderIcon /> : <CalendarMonthIcon />}
                  </IconButton>
                </Box>

                {isCalendarMode ? (
                  <Calendar
                    user={selectedUser}
                    handleDateChange={handleDateChange}
                    highlightedDays={highlightedDays}
                    setHighlightedDays={setHighlightedDays}
                  />
                ) : (
                  <CalendarList user={selectedUser} />
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {orderToEdit && (
        <EditOrderFormDialog
          open={openEditOrderDialog}
          handleClose={handleCloseEditOrderDialog}
          row={orderToEdit}
          onSave={handleSaveEditOrder}
          onRefresh={handleRefresh}
          onDateChange={(newDate: Dayjs) => {
            setHighlightedDays((prev) => {
              const updatedDays = [...prev];

              const oldDateIndex = updatedDays.findIndex((day) =>
                day.isSame(dayjs(orderToEdit.date), "day"),
              );

              if (oldDateIndex !== -1) {
                updatedDays.splice(oldDateIndex, 1);
              }

              updatedDays.push(newDate);

              return updatedDays;
            });
          }}
        />
      )}

      {(config.role === "admin" || config.role === "manager") && (
        <>
          <AddItemFormDialog
            open={openCreateDialog}
            handleClose={handleCloseCreateDialog}
            onItemAdded={handleItemAdded}
          />
          <OrderItemFormDialog
            open={openOrderItemDialog}
            handleClose={handleCloseOrderItemDialog}
          />
          <AddOrderFormDialog
            open={openCreateOrderDialog}
            handleClose={handleCloseCreateOrderDialog}
            addOrder={handleAddOrder}
            onRefresh={handleRefresh}
          />

          {orderOrderItemToAdd && (
            <AddOrderOrderItemFormDialog
              open={openAddOrderOrderItemDialog}
              handleClose={handleCloseAddOrderOrderItemDialog}
              order={orderOrderItemToAdd}
              addOrderOrderItems={handleAddOrderOrderItem}
            />
          )}
          {orderToDelete && (
            <DeleteFormDialog
              open={openDeleteDialog}
              handleClose={handleCloseDeleteDialog}
              itemType="order"
              itemId={orderToDelete.order_id}
              onDelete={handleDeleteOrderClick}
            />
          )}

          {orderToDelete && orderOrderItemToDelete && (
            <DeleteFormDialog
              open={openDeleteOrderOrderItemDialog}
              handleClose={handleCloseDeleteOrderOrderItemDialog}
              itemType="order_order_item"
              itemId={orderOrderItemToDelete.order_order_item_id}
              onDelete={handleDeleteOrderOrderItemClick}
              onEdit={handleSaveEditOrder}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default OrderPage;
